import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useRepayVaroLineOfCredit from 'features/lineOfCredit/hooks/useRepayVaroLineOfCredit';
import { VLOCContext } from 'features/lineOfCredit/providers/VLOCContextProvider/VLOCContextProvider';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import TransferField from 'shared/components/vxl/fields/TransferField/TransferField';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import NumberPad from 'shared/components/vxl/NumberPad/NumberPad';
import {
  amountStyles,
  TransactionListItemProps,
} from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import ExpressiveXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXLText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

const VLOCPaymentDetailsScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'repaymentDetails',
  });

  const { bottom } = useSafeAreaInsets();

  const { setVLOCContextData } = useContext(VLOCContext);

  const {
    bottomSheetRef,
    handleNumPadAction,
    handleAccountSelect,
    isNextDisabled,
    limitErrorString,
    lineOfCreditPaymentView,
    openBottomSheet,
    paymentAccountOptions,
    paymentAlert,
    selectedSourceAccount,
    transferAmount,
    outstandingBalance,
    amountDue,
    loading,
    error,
  } = useRepayVaroLineOfCredit();

  const repayAccountSelectedEvent = usePressableAnalytics({
    analyticsId: 'repayAccountSelected',
  });

  const paymentType = (): string => {
    if (outstandingBalance === transferAmount) {
      return 'total';
    } else if (Number(amountDue) > Number(transferAmount)) {
      return 'partial';
    } else if (transferAmount === amountDue) {
      return 'full';
    } else if (
      Number(amountDue) < Number(transferAmount) &&
      Number(transferAmount) < Number(outstandingBalance)
    ) {
      return 'overflow';
    }
    return '';
  };

  const handleNextAction = () => {
    setVLOCContextData({
      amount: transferAmount,
      lineOfCreditId: lineOfCreditPaymentView?.lineOfCreditId ?? '',
      sourceAccount: selectedSourceAccount ?? undefined,
    });
    router.push({
      pathname: `./confirm-payment`,
    });
  };

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(error ? goHome : goBack);

  const hasMultiplePaymentAccountOptions = paymentAccountOptions.length > 1;

  const transactions: TransactionListItemProps[] = paymentAccountOptions?.map(
    (account) => {
      return {
        titleText: `${account?.nickname} • ${account?.accountNumberLast4}`,
        onPress: () => handleAccountSelect(account),
        secondaryText: account?.availableBalance?.formatted
          ? `${translatedText('available')} ${account?.availableBalance?.formatted}`
          : '',
        isPending: false,
        amountText: '',
        amountStyle: amountStyles.NEUTRAL,
        iconAvatar: {
          variant: 'MD',
          imageSource: { uri: account?.iconURL },
        },
      };
    },
  );

  return (
    <View style={[styles.mainContainer, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('navigationTitle'),
          headerBackVisible: !error,
          headerShown: !error,
          gestureEnabled: !error,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView contentContainerStyle={styles.scrollViewContainer}>
        <TransferField
          primaryText={
            selectedSourceAccount
              ? `${selectedSourceAccount.nickname} • ${selectedSourceAccount.accountNumberLast4}`
              : translatedText('selectAnAccount')
          }
          secondaryText={
            selectedSourceAccount &&
            `${translatedText('available')} ${selectedSourceAccount.availableBalance.formattedWithPlusPrefix}`
          }
          labelText={translatedText('from')}
          avatarAccessory={
            selectedSourceAccount
              ? {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-varo-logo',
                  props: {
                    iconAccessibilityLabel: 'logo',
                    imageSource: { uri: selectedSourceAccount?.iconURL },
                    variant: 'MD',
                  },
                }
              : {
                  name: VXLComponentId.ICON_AVATAR,
                  id: 'transfer-money-source-bank-icon',
                  props: {
                    iconAccessibilityLabel: 'bank-icon',
                    iconKey: 'BANK',
                    variant: 'MD',
                  },
                }
          }
          showChevron={hasMultiplePaymentAccountOptions}
          isCollapsed={hasMultiplePaymentAccountOptions}
          onPress={() => {
            if (hasMultiplePaymentAccountOptions) {
              repayAccountSelectedEvent();
              openBottomSheet();
            }
          }}
        />
        <View style={styles.amountContainer}>
          <ExpressiveXXLText numberOfLines={0} style={styles.amountTextStyle}>
            {formatCurrency(transferAmount)}
          </ExpressiveXXLText>
        </View>
        <View style={styles.footerContainer}>
          {paymentAlert.message ? (
            <View style={styles.alertContainer}>
              <InlineAlert
                bodyText={paymentAlert.message}
                alertType={paymentAlert.type}
              />
            </View>
          ) : null}
          <BodyXSText style={styles.limitTextStyle}>
            {limitErrorString}
          </BodyXSText>
        </View>
      </ScrollView>
      <View style={styles.numPadContainer}>
        <NumberPad
          submitButtonLabel={translatedText('buttonLabel')}
          handleButtonPress={handleNumPadAction}
          handleSubmit={handleNextAction}
          analyticsId="nextButtonClicked"
          customAttributes={{
            amount: transferAmount,
            payment_type: paymentType(),
          }}
          submitDisabled={isNextDisabled}
        />
      </View>

      {paymentAccountOptions ? (
        <BottomSheet ref={bottomSheetRef}>
          <TransactionsSectionList transactions={transactions} />
        </BottomSheet>
      ) : null}

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-VLOCpaymentDetailsScreen"
              trackingAnalyticsId="payment-details-error-screen-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_REPAYMENT_AMOUNT,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  alertContainer: {
    alignItems: 'center',
    padding: theme.spacing.m,
    width: '100%',
  },
  amountContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  amountTextStyle: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.xxl,
    textAlign: 'center',
  },
  footerContainer: {
    alignItems: 'center',
    paddingBottom: theme.spacing.s,
  },
  limitTextStyle: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  numPadContainer: {
    paddingHorizontal: theme.spacing.m,
    width: '100%',
  },
  scrollViewContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
});

export default VLOCPaymentDetailsScreen;
